import React, { useState, useEffect } from "react";
import { memberFetch, getMemberToken } from "../../lib/memberApi";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { Dumbbell, Activity, Heart, Zap, Loader2, Sparkles, ShieldAlert, Printer } from "lucide-react";
import CustomizerChecklist from "./CustomizerChecklist";

const WORKOUT_CATEGORIES = [
  { id: "strength", label: "Strength", Icon: Dumbbell, desc: "Compound lifts, accessories, finishers" },
  { id: "cardio", label: "Cardio", Icon: Activity, desc: "Intervals, sprints, steady state, plyo" },
  { id: "speed_agility", label: "Speed & Agility", Icon: Zap, desc: "Sprints + cuts + weight-room transfer" },
  { id: "recovery", label: "Recovery", Icon: Heart, desc: "Mobility, breathwork, soft tissue" },
];

const SPEED_FOCUS_OPTIONS = [
  { value: "top_speed", label: "Top Speed — max-velocity sprints, fly-zones, wickets" },
  { value: "acceleration", label: "Acceleration — explosive first 3 steps, sled, hills" },
  { value: "change_of_direction", label: "Change of Direction — pro-agility, L-drill, footwork" },
  { value: "long_distance", label: "Long Distance — endurance, tempo, VO2max, runner conditioning" },
];

const CATEGORY_LABEL = {
  strength: "Strength",
  cardio: "Cardio",
  speed_agility: "Speed & Agility",
  recovery: "Recovery",
};

const SPEED_FOCUS_LABEL = {
  top_speed: "Top Speed",
  acceleration: "Acceleration",
  change_of_direction: "Change of Direction",
  long_distance: "Long Distance",
};

const prettyCategory = (cat, focus) => {
  const base = CATEGORY_LABEL[cat] || (cat || "").replace(/_/g, " ");
  return focus ? `${base} · ${SPEED_FOCUS_LABEL[focus] || focus}` : base;
};

export default function WorkoutsTab({ me, refreshMe }) {
  const [cat, setCat] = useState(me?.active_workout_category || "strength");
  const [plans, setPlans] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [opts, setOpts] = useState({});
  const setOpt = (id, value) => setOpts((o) => ({ ...o, [id]: value }));
  const [openPlan, setOpenPlan] = useState(null);
  const [openWorkout, setOpenWorkout] = useState({});
  const [blurred, setBlurred] = useState(false);
  const quota = me?.quotas?.workout_plans || { used: 0, limit: 3, resets_at: null };
  const unlimited = quota.limit === null || quota.limit === undefined;
  const reachedLimit = !unlimited && quota.used >= quota.limit;
  const resetsLabel = quota.resets_at
    ? new Date(quota.resets_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  // Screenshot/share deterrents
  useEffect(() => {
    const onBlur = () => setBlurred(true);
    const onFocus = () => setBlurred(false);
    const onVis = () => setBlurred(document.hidden);
    const onKey = (e) => {
      // Best-effort: nuke common save/print hotkeys
      if ((e.ctrlKey || e.metaKey) && ["s", "p"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        toast.error("Saving/printing the workouts is disabled.");
      }
    };
    const onCtx = (e) => e.preventDefault();
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("keydown", onKey);
    document.addEventListener("contextmenu", onCtx);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("contextmenu", onCtx);
    };
  }, []);

  const load = async () => {
    try {
      // Member only ever has at most one active workout plan now
      const items = await memberFetch(`/member/workout-plans`);
      setPlans(items);
      if (items.length > 0) setOpenPlan(items[0]);
      else setOpenPlan(null);
    } catch (err) {
      console.error("Failed to load workout plans:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (reachedLimit) {
      toast.error(`You've used all ${quota.limit} workout generations this 30-day window. Resets ${resetsLabel || "soon"}.`);
      return;
    }
    setGenerating(true);
    try {
      const body = { category: cat };
      if (opts.fitness_level) body.fitness_level = opts.fitness_level;
      if (opts.location) body.location = opts.location;
      if (opts.equipment) body.equipment = opts.equipment;
      if (opts.injuries) body.injuries = typeof opts.injuries === "string" ? opts.injuries : "";
      if (cat === "speed_agility" && opts.speed_focus) body.speed_focus = opts.speed_focus;
      const data = await memberFetch("/member/workout-plan", {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success("12 workouts ready");
      if (refreshMe) await refreshMe();
      await load();
      setOpenPlan(data);
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  const printSession = async (sessionNumber, workoutName) => {
    try {
      const token = getMemberToken();
      const res = await fetch(`${API}/member/workout-plan/session/${sessionNumber}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (workoutName || `Day-${sessionNumber}`).replace(/[^\w-]+/g, "-").slice(0, 50);
      a.download = `GainTime-Day${sessionNumber}-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Day ${sessionNumber} downloaded`);
    } catch (e) {
      toast.error(e.message || "Could not download workout");
    }
  };

  return (
    <div data-testid="tab-workouts" className="select-none">
      <style>{`
        @media print {
          [data-testid="tab-workouts"] { display: none !important; }
          body::after { content: "Workouts cannot be printed."; display:block; font-family: sans-serif; padding: 40px; }
        }
        [data-testid="tab-workouts"] * {
          -webkit-user-select: none;
          -moz-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-user-drag: none;
        }
      `}</style>

      <h2 className="font-display text-3xl mb-2 flex items-center gap-3">
        Workouts
        <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-zinc-500 border border-zinc-800 px-2 py-0.5">
          <ShieldAlert size={11} className="text-[#E5B43A]" /> View only
        </span>
      </h2>
      <p className="text-zinc-500 text-sm mb-2">One active plan at a time. Up to <b className="text-white">3 generations</b> per 30-day window — each new plan replaces the previous one.</p>
      <p className="text-xs text-zinc-600 italic mb-8">Workouts are view-only. Screenshots / saving / printing disabled.</p>

      {/* Category selector for the NEXT generation */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Pick Focus for Next Generation</div>
      <div className="flex gap-1 border-b border-zinc-800 mb-8 overflow-x-auto">
        {WORKOUT_CATEGORIES.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setCat(id)}
            data-testid={`workout-cat-${id}`}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-[0.2em] uppercase whitespace-nowrap border-b-2 transition-colors ${cat === id ? "border-[#E5B43A] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-7 border border-zinc-800 bg-[#141414] p-6 md:p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">{WORKOUT_CATEGORIES.find(c => c.id === cat)?.label}</div>
          <div className="font-display text-2xl mb-2">{WORKOUT_CATEGORIES.find(c => c.id === cat)?.desc}</div>
          <p className="text-zinc-500 text-sm">{plans.length > 0 ? `Currently active: ${prettyCategory(plans[0].category, plans[0].speed_focus)}` : "No plan yet — generate one below."}</p>
        </div>
        <div className="lg:col-span-5 border border-[#E5B43A] bg-[#141414] p-6 md:p-8 flex flex-col">
          <Dumbbell className="text-[#E5B43A] mb-4" size={28} />
          <h3 className="font-display text-2xl mb-3">Generate 12 Workouts</h3>
          <p className="text-zinc-400 text-sm flex-1 leading-relaxed">
            Bold, dynamic sessions built for <b className="text-white">{WORKOUT_CATEGORIES.find(c => c.id === cat)?.label}</b>.
          </p>

          <div className="mt-4 border border-zinc-800 bg-[#0F0F0F] px-3 py-2 flex items-center justify-between text-[10px] tracking-[0.2em] uppercase" data-testid="workout-quota">
            <span className="text-zinc-500">Generations</span>
            <span className={reachedLimit ? "text-[#E5B43A] font-bold" : "text-zinc-300 font-bold"}>
              {unlimited ? "Unlimited" : `${quota.used} / ${quota.limit}${resetsLabel ? ` · resets ${resetsLabel}` : ""}`}
            </span>
          </div>

          <button
            onClick={generate}
            disabled={generating || reachedLimit}
            data-testid="generate-workout-plan-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#E5B43A] disabled:hover:text-white"
          >
            {generating
              ? <><Loader2 className="animate-spin" size={16} /> Building...</>
              : reachedLimit
                ? <>Limit Reached</>
                : <><Sparkles size={16} /> {plans.length > 0 ? "Replace Current Plan" : "Generate Plan"}</>}
          </button>

          <div className="mt-3">
            <CustomizerChecklist
              testIdPrefix="workout-opts"
              title="Personalize this plan"
              values={opts}
              onChange={setOpt}
              options={[
                ...(cat === "speed_agility" ? [{
                  id: "speed_focus", kind: "select", label: "Speed Focus",
                  selectOptions: SPEED_FOCUS_OPTIONS,
                }] : []),
                { id: "fitness_level", kind: "select", label: "Fitness Level",
                  selectOptions: [
                    { value: "beginner", label: "Beginner — low impact, mobility-first" },
                    { value: "intermediate", label: "Intermediate — mid intensity + finishers" },
                    { value: "master", label: "Master — mid-to-high intensity, challenge me" },
                  ]},
                { id: "location", kind: "select", label: "Where will you train?",
                  selectOptions: cat === "speed_agility" ? [
                    { value: "track", label: "Track / field only (no weight room)" },
                    { value: "track_and_gym", label: "Track + weight room (both — recommended)" },
                    { value: "gym", label: "Weight room only (no track access)" },
                    { value: "home", label: "Home / driveway (limited space)" },
                  ] : [
                    { value: "home", label: "Home (limited space)" },
                    { value: "gym", label: "Gym (rack, machines, full kit)" },
                  ]},
                { id: "equipment", kind: "text", label: "Equipment available",
                  placeholder: "e.g. dumbbells 5-50lb, kettlebell 35lb, bands" },
                { id: "injuries", kind: "check", label: "I have an injury or area of pain",
                  hint: "We'll avoid loading that area and offer modifications instead.",
                  requiresInput: true,
                  placeholder: "e.g. right knee, lower back, left shoulder" },
              ]}
            />
          </div>
          {plans.length > 0 && !reachedLimit && (
            <p className="mt-2 text-[10px] tracking-widest uppercase text-zinc-500">Generating again will replace your current plan below.</p>
          )}
        </div>
      </div>

      {/* Active plan (only one) */}
      {plans.length === 0 ? (
        <p className="text-zinc-500 text-sm italic">No active workout plan yet. Generate one above.</p>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => {
            const isOpen = openPlan?.id === p.id;
            const dl = daysLeft(p.expires_at);
            return (
              <div key={p.id} className="border border-zinc-800 bg-[#141414]" data-testid={`workout-plan-${p.id}`}>
                <button
                  onClick={() => setOpenPlan(isOpen ? null : p)}
                  className="w-full px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-[#1a1a1a] transition-colors"
                  data-testid={`workout-plan-toggle-${p.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 min-w-0 flex-1 w-full">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] shrink-0 truncate" data-testid={`workout-plan-category-${p.id}`}>
                      {prettyCategory(p.category, p.speed_focus)}
                    </div>
                    <div className="font-display text-base shrink-0">{p.workouts?.length || 0} workouts</div>
                    <div className="hidden md:block text-xs text-zinc-500 truncate flex-1 text-left">
                      {(p.workouts || []).slice(0, 3).map((w) => w.name).join(" · ")}{p.workouts?.length > 3 ? " …" : ""}
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto shrink-0">
                    <span
                      className={`inline-flex items-baseline gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 border ${
                        dl > 7 ? "border-zinc-700 bg-zinc-900/60 text-zinc-200" : "border-[#E5B43A] bg-[#E5B43A]/10 text-[#E5B43A]"
                      }`}
                      data-testid={`workout-plan-days-left-${p.id}`}
                    >
                      <span className="font-display text-base leading-none">{dl}</span>
                      <span>day{dl === 1 ? "" : "s"} left</span>
                    </span>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#E5B43A]">{isOpen ? "Close" : "Open"}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-zinc-900 p-5 relative">
                    {/* Watermark */}
                    <div className="pointer-events-none absolute inset-0 grid place-items-center opacity-[0.04]">
                      <div className="text-6xl md:text-9xl font-display rotate-[-20deg] text-white whitespace-nowrap">GAIN TIME</div>
                    </div>
                    {/* Blur overlay when window not focused */}
                    {blurred && (
                      <div className="absolute inset-0 bg-[#0A0A0A]/95 backdrop-blur-xl grid place-items-center z-10">
                        <div className="text-center">
                          <ShieldAlert className="mx-auto text-[#E5B43A] mb-3" size={32} />
                          <div className="font-display text-xl">Workout Hidden</div>
                          <div className="text-xs text-zinc-500 mt-2">Bring the tab back into focus to view.</div>
                        </div>
                      </div>
                    )}

                    <div className="relative z-0 space-y-3">
                      {(p.workouts || []).map((w, idx) => {
                        const key = `${p.id}-${idx}`;
                        const open = !!openWorkout[key];
                        return (
                          <div key={key} className="border border-zinc-800 bg-[#0F0F0F]" data-testid={`workout-${p.id}-${idx}`}>
                            <button
                              onClick={() => setOpenWorkout((s) => ({ ...s, [key]: !s[key] }))}
                              className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#1a1a1a] text-left"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {w.phase && (
                                    <span className="text-[9px] tracking-[0.25em] uppercase border border-[#E5B43A]/60 text-[#E5B43A] px-1.5 py-0.5">{w.phase}</span>
                                  )}
                                  <div className="font-display text-base truncate">Day {w.session_number || idx + 1} · {w.name}</div>
                                </div>
                                <div className="text-xs text-zinc-500 truncate mt-1">{w.focus}</div>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 text-[10px] tracking-widest uppercase">
                                <span className="border border-zinc-700 px-2 py-0.5 text-zinc-400">{w.duration_min || "—"} min</span>
                                <span className={`px-2 py-0.5 ${w.intensity === "High" || w.intensity === "Max" ? "bg-[#E5B43A] text-black" : "border border-zinc-700 text-zinc-400"}`}>{w.intensity}</span>
                              </div>
                            </button>
                            {open && (
                              <div className="px-4 py-4 border-t border-zinc-900 space-y-4">
                                {w.equipment?.length > 0 && (
                                  <div className="text-xs text-zinc-400"><span className="text-[10px] tracking-widest uppercase text-zinc-500 mr-2">Equipment</span>{w.equipment.join(", ")}</div>
                                )}
                                {w.warmup?.length > 0 && (
                                  <div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">Warm-Up</div>
                                    <ul className="text-sm text-zinc-300 space-y-1">
                                      {w.warmup.map((x) => <li key={`warm-${x.name}-${x.detail}`} className="flex flex-wrap gap-x-2 gap-y-1"><span className="text-zinc-600 shrink-0">·</span><b className="text-white">{x.name}</b><span className="text-zinc-400 break-words min-w-0">— {x.detail}</span></li>)}
                                    </ul>
                                  </div>
                                )}
                                {w.main?.length > 0 && (
                                  <div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-3">Main Block</div>
                                    <ol className="space-y-3">
                                      {w.main.map((m, i) => (
                                        <li key={`${w.name}-main-${m.name}-${i}`} className="border border-zinc-800 bg-[#141414] px-4 py-3">
                                          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                                            <div className="font-display text-base text-white break-words min-w-0">{i + 1}. {m.name}</div>
                                            <div className="flex gap-2 text-[10px] tracking-widest uppercase shrink-0">
                                              <span className="border border-zinc-700 px-2 py-0.5 text-zinc-300">{m.sets || "—"} × {m.reps || "—"}</span>
                                              {m.rest && <span className="border border-zinc-700 px-2 py-0.5 text-zinc-400">{m.rest} rest</span>}
                                            </div>
                                          </div>
                                          {m.how_to && (
                                            <p className="text-sm text-zinc-300 leading-relaxed mb-2 break-words">{m.how_to}</p>
                                          )}
                                          {m.cues && (
                                            <p className="text-xs text-[#E5B43A] italic break-words"><span className="text-zinc-500 not-italic tracking-widest uppercase mr-2">Cue</span>{m.cues}</p>
                                          )}
                                        </li>
                                      ))}
                                    </ol>
                                  </div>
                                )}
                                {w.finisher_or_cooldown?.length > 0 && (
                                  <div>
                                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">Finisher / Cooldown</div>
                                    <ul className="text-sm text-zinc-300 space-y-1">
                                      {w.finisher_or_cooldown.map((x) => <li key={`fin-${x.name}-${x.detail}`} className="flex flex-wrap gap-x-2 gap-y-1"><span className="text-zinc-600 shrink-0">·</span><b className="text-white">{x.name}</b><span className="text-zinc-400 break-words min-w-0">— {x.detail}</span></li>)}
                                    </ul>
                                  </div>
                                )}
                                {w.notes && <p className="text-xs text-zinc-500 italic border-t border-zinc-800 pt-3">💡 {w.notes}</p>}
                                <div className="pt-3 border-t border-zinc-900 flex justify-end">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      printSession(w.session_number || (idx + 1), w.name);
                                    }}
                                    data-testid={`print-workout-${p.id}-${idx}`}
                                    className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-800 px-3 py-2 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
                                  >
                                    <Printer size={11} /> Print Day {w.session_number || idx + 1} PDF
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
